/**
 * International ATC Redirect — Shopify Horizon Theme
 * 
 * HOW TO INSTALL:
 * 1. Go to Online Store → Themes → Edit code
 * 2. Open Assets → Add a new file → name it "atc-redirect.js"
 * 3. Paste this entire file
 * 4. Open Layout → theme.liquid
 * 5. Just before </body>, add:
 *    <script src="{{ 'atc-redirect.js' | asset_url }}" defer></script>
 * 6. Update REDIRECT_COUNTRIES if needed
 *
 * NOTES:
 * - Uses Shopify.country (set by Shopify Markets — no API call needed)
 * - Falls back to graceful pass-through if country can't be detected
 * - Works on product pages AND quick-add buttons in collection pages
 */

(function () {
  // ─── CONFIG ──────────────────────────────────────────────────────────────────

  const OTHER_STORE_URL = 'https://www.hockeystickman.com/collections/pro-blackout-hockey-sticks';

  // Countries to redirect away from the Canada store.
  // Full ISO 3166-1 alpha-2 list: https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
  const REDIRECT_COUNTRIES = [
    'US', // United States
    'GB', // United Kingdom
    'AU', // Australia
    'NZ', // New Zealand
    'DE', // Germany
    // Add more as needed...
  ];

  // ─── HELPERS ─────────────────────────────────────────────────────────────────

  /**
   * Get visitor country from Shopify's built-in Markets detection.
   * Returns a 2-letter country code (e.g. "US") or null if unavailable.
   */
  function getShopifyCountry() {
    // Shopify.country is set when Markets is configured and geo-detection is on.
    // It reflects the detected/selected country for the current session.
    return (
      window.Shopify?.country ||       // primary — set by Markets
      window.Shopify?.locale?.country || // fallback locale object
      null
    );
  }

  /**
   * Always redirects to the fixed collection URL on the other store.
   * Product names differ between stores so we don't attempt handle matching.
   */
  function buildRedirectUrl() {
    return OTHER_STORE_URL;
  }

  /**
   * Show a non-blocking banner instead of a silent redirect.
   * Gives the user context and a clear CTA rather than just yanking them away.
   */
  function showRedirectBanner(url) {
    // Don't show twice
    if (document.getElementById('intl-redirect-banner')) return;

    const banner = document.createElement('div');
    banner.id = 'intl-redirect-banner';
    banner.innerHTML = `
      <style>
        #intl-redirect-banner-inner {
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%);
          background: #1a1a1a;
          color: #fff;
          padding: 16px 20px;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          font-family: inherit;
          font-size: 14px;
          z-index: 99999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          width: min(420px, 92vw);
          box-sizing: border-box;
          text-align: left;
        }
        #intl-redirect-banner-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        #intl-redirect-banner-actions a {
          background: #fff;
          color: #1a1a1a;
          padding: 9px 18px;
          border-radius: 4px;
          text-decoration: none;
          font-weight: 600;
          white-space: nowrap;
          flex: 1;
          text-align: center;
        }
        #intl-redirect-banner-actions button {
          background: transparent;
          color: #aaa;
          border: 1px solid #555;
          padding: 9px 14px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
          white-space: nowrap;
          flex-shrink: 0;
        }
      </style>
      <div id="intl-redirect-banner-inner">
        <span>🌎 Looks like you're outside Canada — we'll take you to our international store.</span>
        <div id="intl-redirect-banner-actions">
          <a href="${url}">Shop International Store →</a>
          <button onclick="document.getElementById('intl-redirect-banner').remove()">Stay</button>
        </div>
      </div>
    `;

    document.body.appendChild(banner);
  }

  // ─── MAIN INTERCEPT ───────────────────────────────────────────────────────────

  function interceptATCButtons() {
    const country = getShopifyCountry();

    // If country isn't detected or is Canada, do nothing — normal ATC flow
    if (!country || !REDIRECT_COUNTRIES.includes(country)) return;

    /**
     * Horizon ATC button selectors:
     * - [data-add-to-cart]         → main product page button (Horizon specific)
     * - [name="add"]               → standard Shopify form submit
     * - .quick-add__submit         → quick-add modal button
     * - product-form [type="submit"] → web component submit
     */
    const ATC_SELECTORS = [
      '[data-add-to-cart]',
      'button[name="add"]',
      '.quick-add__submit',
      'product-form button[type="submit"]',
    ].join(', ');

    // Use event delegation on document so dynamically loaded quick-add buttons
    // (e.g. from collection page modals) are also caught
    document.addEventListener('click', function (e) {
      const btn = e.target.closest(ATC_SELECTORS);
      if (!btn) return;

      // Don't double-fire if we already handled it
      if (btn.dataset.redirectHandled) return;
      btn.dataset.redirectHandled = 'true';

      e.preventDefault();
      e.stopImmediatePropagation();

      const url = buildRedirectUrl();
      showRedirectBanner(url);
    }, true); // capture phase so we fire before Horizon's own handlers
  }

  // ─── INIT ─────────────────────────────────────────────────────────────────────

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', interceptATCButtons);
  } else {
    interceptATCButtons();
  }
})();